import React, { useState, useCallback, useContext } from "react";
import { Link } from "gatsby";
import { searchByImageVector, searchByText } from "../services/api";
import { AuthUserContext } from "../context/AuthUser";
import ImageSearchSuggestion from "../components/cards/ImageSearchSuggestion";
import { markdownToHtml } from "../utils/markdownToHtml";
import { BsCamera, BsImage, BsUpload } from "react-icons/bs";
import { Container } from "../components/styled/ProductsStyled";

const MAX_FILE_SIZE_MB = 10;
const MAX_IMAGE_DIMENSION = 1200;
const JPEG_QUALITY = 0.8;

const getErrorMessage = (value) => {
  if (!value) return "Something went wrong. Please try again.";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.message || value.error || JSON.stringify(value);
  }
  return String(value);
};

const resizeAndCompressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
            width = MAX_IMAGE_DIMENSION;
          } else {
            width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
            height = MAX_IMAGE_DIMENSION;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const base64 = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        resolve(base64);
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const ImageSearch = () => {
  const { authUser } = useContext(AuthUserContext);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const [searchMode, setSearchMode] = useState("image");
  const [textQuery, setTextQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [userComment, setUserComment] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState("");
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [historyId, setHistoryId] = useState(null);

  const validateAndProcessImage = async (file) => {
    setError(null);
    setAnalysis("");
    setResults([]);
    setHasSearched(false);

    if (!file || !file.type.startsWith("image/")) {
      setError("Please upload a valid image file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Image must be smaller than ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    try {
      const originalUrl = URL.createObjectURL(file);
      setPreview(originalUrl);

      const compressedBase64 = await resizeAndCompressImage(file);
      setImageData(compressedBase64);
    } catch (err) {
      setError("Failed to process image. Please try another one.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) validateAndProcessImage(file);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) validateAndProcessImage(file);
  }, []);

  const resetSearchState = () => {
    setError(null);
    setAnalysis("");
    setResults([]);
    setHasSearched(false);
    setHistoryId(null);
  };

  const handleSearch = async () => {
    if (!token) {
      window.location.href = "/login";
      return;
    }

    if (searchMode === "image") {
      if (!imageData) return;

      const trimmedComment = userComment.trim();
      if (!trimmedComment) {
        setError("Please add a comment describing what you are looking for.");
        return;
      }

      setAnalyzing(true);
      resetSearchState();

      try {
        const result = await searchByImageVector(imageData, token, trimmedComment);
        setAnalyzing(false);
        setHasSearched(true);

        if (!result.success) {
          setError(getErrorMessage(result.error));
          return;
        }

        setAnalysis(result.analysis || "");
        setResults(result.results || []);
        setHistoryId(result.historyId || null);
      } catch (err) {
        setError(getErrorMessage(err));
        setAnalyzing(false);
      }
    } else {
      const trimmedQuery = textQuery.trim();
      if (!trimmedQuery) {
        setError("Please describe the fabric you are looking for.");
        return;
      }

      setAnalyzing(true);
      resetSearchState();

      try {
        const result = await searchByText(trimmedQuery, token);
        setAnalyzing(false);
        setHasSearched(true);

        if (!result.success) {
          setError(getErrorMessage(result.error));
          return;
        }

        setAnalysis(result.analysis || "");
        setResults(result.results || []);
        setHistoryId(result.historyId || null);
      } catch (err) {
        setError(getErrorMessage(err));
        setAnalyzing(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setPreview(null);
    setImageData(null);
    setUserComment("");
    resetSearchState();
  };

  const handleModeChange = (mode) => {
    setSearchMode(mode);
    resetSearchState();
    setTextQuery("");
    setUserComment("");
    setPreview(null);
    setImageData(null);
  };

  const showResults = hasSearched && !analyzing;
  const hasResults = results.length > 0;
  const analysisHtml = markdownToHtml(analysis);
  const showAnalysisPanel = analyzing || analysis;

  const isSearchDisabled = analyzing || (searchMode === "image" ? !imageData || !userComment.trim() : !textQuery.trim());

  return (
    <div className="image-search-page">
      <div className="image-search-hero">
        <h1>Find similar fabrics</h1>

        <div className="image-search-tabs">
          <button
            type="button"
            className={`image-search-tab ${searchMode === "image" ? "active" : ""}`}
            onClick={() => handleModeChange("image")}
          >
            By image
          </button>
          <button
            type="button"
            className={`image-search-tab ${searchMode === "text" ? "active" : ""}`}
            onClick={() => handleModeChange("text")}
          >
            By description
          </button>
        </div>

        <div className={`image-search-layout ${showAnalysisPanel ? "has-analysis" : ""}`}>
          <div className="image-search-layout__left">
            {searchMode === "image" ? (
              <>
                <div
                  className={`image-search-dropzone ${imageData ? "has-image" : ""}`}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {!imageData ? (
                    <>
                      <BsImage className="dropzone-icon" />
                      <p>Drag & drop a fabric image here</p>
                      <span>or</span>
                      <label className="btn-upload">
                        <BsUpload /> Browse files
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          hidden
                        />
                      </label>
                    </>
                  ) : (
                    <div className="image-preview-wrapper">
                      <img src={preview} alt="Uploaded fabric" className="image-preview" />
                      <button className="btn-remove-image" onClick={handleRemoveImage}>
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {imageData && (
                  <>
                    <label htmlFor="user-comment" className="image-search-comment__label">
                      Add details to improve results
                    </label>
                    <textarea
                      id="user-comment"
                      className="image-search-comment"
                      placeholder="Add details, e.g. stretch material, weight 90g, cotton, etc."
                      value={userComment}
                      onChange={(e) => setUserComment(e.target.value)}
                      rows={4}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <label htmlFor="text-query" className="image-search-comment__label">
                  Describe the fabric you are looking for
                </label>
                <textarea
                  id="text-query"
                  className="image-search-comment"
                  placeholder="Add details, e.g. stretch material, weight 90g, cotton, etc."
                  value={textQuery}
                  onChange={(e) => setTextQuery(e.target.value)}
                  rows={6}
                />
              </>
            )}

            {error && <p className="image-search-error">{error}</p>}

            <button
              className="btn-search-image"
              onClick={handleSearch}
              disabled={isSearchDisabled}
            >
              {analyzing ? (
                <>
                  <BsCamera /> Searching...
                </>
              ) : (
                <>
                  <BsCamera /> Find similar fabrics
                </>
              )}
            </button>

            {!analyzing && !hasSearched && (
              <Link to="/member/history" className="image-search-history-link">
                AI search history
              </Link>
            )}
          </div>

          {showAnalysisPanel && (
            <div className="image-search-layout__right">
              {analyzing && (
                <div className="image-search-analysis image-search-analysis--loading">
                  <p>Analyzing and finding similar fabrics...</p>
                </div>
              )}

              {analysis && !analyzing && (
                <div className="image-search-analysis">
                  <div
                    className="image-search-analysis__content"
                    dangerouslySetInnerHTML={{ __html: analysisHtml }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showResults && (
        <div className="image-search-results">
          <Container className="container-fluid">
            {!hasResults ? (
              <p className="image-search-no-results">
                No similar fabrics found. Try a different {searchMode === "image" ? "image or comment" : "description"}.
              </p>
            ) : (
              <div className="products-grid">
                {results.map((item) => (
                  <ImageSearchSuggestion
                    key={item.product.id}
                    product={item.product}
                    score={item.score}
                    matchReason={item.reason}
                    matchPoints={item.matchPoints}
                    historyId={historyId}
                    token={token}
                    isLoggedIn={authUser?.logged}
                  />
                ))}
              </div>
            )}
          </Container>
        </div>
      )}
    </div>
  );
};

export default ImageSearch;
